/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';

const messages = defineMessages({
    languageStudio: {
        id: 'qnaMaker.qnaMakerBreadcrumb.languageStudio',
        defaultMessage: 'Language Studio'
    },
    azureSearch: {
        id: 'qnaMaker.qnaMakerBreadcrumb.azureSearch',
        defaultMessage: 'Azure Search'
    },
    qnaProjects: {
        id: 'qnaMaker.qnaMakerBreadcrumb.qnaProjects',
        defaultMessage: 'Custom question answering'
    },
    manageSources: {
        id: 'qnaMaker.qnaMakerBreadcrumb.manageSources',
        defaultMessage: '{ projectName } - Manage sources'
    },
    edit: {
        id: 'qnaMaker.qnaMakerBreadcrumb.edit',
        defaultMessage: '{ projectName } - Edit knowledge base'
    },
    deploy: {
        id: 'qnaMaker.qnaMakerBreadcrumb.deploy',
        defaultMessage: '{ projectName } - Deploy knowledge base'
    },
    review: {
        id: 'qnaMaker.qnaMakerBreadcrumb.review',
        defaultMessage: '{ projectName } - Review suggestions'
    },
    projectSettings: {
        id: 'qnaMaker.qnaMakerBreadcrumb.projectSettings',
        defaultMessage: '{ projectName } - Project settings'
    },
    details: {
        id: 'qnaMaker.qnaMakerBreadcrumb.details',
        defaultMessage: 'Question pair details'
    },
    breadcrumbsAriaLabel: {
        id: 'qnaMaker.qnaMakerBreadcrumb.breadcrumbsAriaLabel',
        defaultMessage: 'Main Breadcrumb'
    },
    moreLinksAriaLabel: {
        id: 'qnaMaker.qnaMakerBreadcrumb.moreLinksAriaLabel',
        defaultMessage: 'More Links'
    }
});

const { getClassName } = getStylistV2('QnaMakerBreadcrumb');

const itemClassName = getClassName('item', important({ fontSize: '14px', fontWeight: 'normal' }));
const linkItemClassName = getClassName('linkItem', theme =>
    important({ color: theme.linkColor, fontSize: '14px', display: 'inline-block', height: '100%' })
);
const linkIconClassName = getClassName('linkIcon', important({ fontSize: '10px' }));

const useBreadcrumbItems = () => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const {
        rootStore: { currentQnaMakerProjectStore }
    } = useGlobalState();

    const navigateTo = (path: string) => () => history.push(path);

    const doesMatch = (path: string, isExact: boolean = true) =>
        doesRouteMatchLocation(path, { exact: isExact, locationToMatch: pathname });

    const items: IBreadcrumbItem[] = [
        { text: localize(messages.languageStudio), key: 'languageStudio', onClick: navigateTo(routeTable.languageStudio.root()) }
    ];

    const projectName = currentQnaMakerProjectStore?.name;

    if (doesMatch(routeTable.qnaMaker.azureSearchPage())) {
        items.push({
            text: localize(messages.azureSearch),
            key: 'azureSearch'
        });
    } else if (doesMatch(routeTable.qnaMaker.projectsPage())) {
        items.push({
            text: localize(messages.qnaProjects),
            key: 'qnaProjects'
        });
    } else if (doesMatch(routeTable.qnaMaker.projectPage(), false)) {
        items.push({
            text: localize(messages.qnaProjects),
            key: 'qnaProjects',
            onClick: navigateTo(routeTable.qnaMaker.projectsPage())
        });

        if (doesMatch(routeTable.qnaMaker.manageSourcesPage(projectName))) {
            items.push({
                text: localize(messages.manageSources, { projectName }),
                key: 'manageSources'
            });
        } else if (doesMatch(routeTable.qnaMaker.editPage(projectName), false)) {
            items.push({
                text: localize(messages.edit, { projectName }),
                key: 'edit'
            });
        } else if (doesMatch(routeTable.qnaMaker.detailsPage(projectName), false)) {
            items.push({
                text: localize(messages.edit, { projectName }),
                key: 'edit',
                onClick: navigateTo(routeTable.qnaMaker.editPage(projectName))
            });

            items.push({
                text: localize(messages.details),
                key: 'details'
            });
        } else if (doesMatch(routeTable.qnaMaker.deployPage(projectName))) {
            items.push({
                text: localize(messages.deploy, { projectName }),
                key: 'deploy'
            });
        } else if (doesMatch(routeTable.qnaMaker.reviewPage(projectName))) {
            items.push({
                text: localize(messages.review, { projectName }),
                key: 'review'
            });
        } else if (doesMatch(routeTable.qnaMaker.settingsPage(projectName))) {
            items.push({
                text: localize(messages.projectSettings, { projectName }),
                key: 'settings'
            });
        }
    }

    return items;
};

export const QnaMakerBreadcrumb = observer(() => {
    const items = useBreadcrumbItems();
    const { localize } = useLocalization();

    return (
        <Breadcrumb
            items={items}
            ariaLabel={localize(messages.breadcrumbsAriaLabel)}
            overflowAriaLabel={localize(messages.moreLinksAriaLabel)}
            styles={{
                root: { margin: 0 },
                item: itemClassName,
                chevron: linkIconClassName,
                itemLink: `${itemClassName} ${linkItemClassName}`
            }}
        />
    );
});
